import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GetMemberDetailsResponse } from "protogen/member_service_pb";
import { useGetMemberDetails } from "services/member";
import { getPreviewStatus } from "utils/previewStatus";
import TokenStore from "services/TokenStore";
import { FamilyStatus } from "protogen/advisors_service_pb";
import {
  memberSelfServeReactivationPath,
  planFlagsFromStatus,
} from "utils/memberFamilyPlan";
import { DEACTIVATION_GRACE_PERIOD_DAYS } from "common/constants";
import { MemberFamilyPlanFlags } from "utils/memberFamilyPlan";
import useDigitalSubscriptionUX from "components/hooks/useDigitalSubscriptionUX";

function withReviewParam(path: string, reviewRequested: boolean): string {
  return reviewRequested ? `${path}?review=true` : path;
}

function isWithinDeactivationGracePeriod(endDate: string | undefined): boolean {
  if (!endDate) return false;
  const end = new Date(endDate);
  const gracePeriodEnd = new Date(end);
  gracePeriodEnd.setDate(
    gracePeriodEnd.getDate() + DEACTIVATION_GRACE_PERIOD_DAYS,
  );
  return new Date() <= gracePeriodEnd;
}

interface MemberDetailsContextType extends MemberFamilyPlanFlags {
  data: GetMemberDetailsResponse | null;
  loading: boolean;
  refresh: () => void;
}

const MemberDetailsContext = createContext<MemberDetailsContextType>({
  data: null,
  loading: true,
  refresh: () => {},
  familyStatus: undefined,
  isDigitalSubscription: false,
  isUnpaidSignup: false,
});

export const useMemberDetails = () => useContext(MemberDetailsContext);

export const MemberDetailsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Captured on mount because MemberHome strips the param from the url before the
  // deactivation redirects below have a chance to run.
  const [reviewRequested] = useState(
    () => new URLSearchParams(location.search).get("review") === "true",
  );
  const [data, setData] = useState<GetMemberDetailsResponse | null>(null);
  const { request, loading, error } = useGetMemberDetails((response) => {
    setData(response);
  });

  const refresh = useCallback(() => {
    request();
  }, [request]);

  useEffect(() => {
    request();
  }, []);

  const familyStatus = TokenStore.getLocalCurrentUser()?.familyStatus;
  const isDigitalSubscription = useDigitalSubscriptionUX();

  const contextValue = useMemo(
    () => ({
      data,
      loading,
      refresh,
      ...planFlagsFromStatus(familyStatus),
      isDigitalSubscription,
    }),
    [data, loading, refresh, familyStatus, isDigitalSubscription],
  );

  useEffect(() => {
    if (error?.includes("has an invalid family status")) {
      const path = error.includes("digital-deactivated")
        ? "/digital-upgrade"
        : "/reactivate";
      navigate(withReviewParam(path, reviewRequested));
    }
  }, [error, navigate, reviewRequested]);

  useEffect(() => {
    if (!data?.family) return;
    const familyStatus = data.family.status;
    const isDeactivated =
      familyStatus === FamilyStatus.DEACTIVATED ||
      familyStatus === FamilyStatus.DIGITAL_DEACTIVATED ||
      familyStatus === FamilyStatus.DIGITAL_DEACTIVATED_TRIAL ||
      familyStatus === FamilyStatus.DEACTIVATED_PROSPECT;
    if (
      isDeactivated &&
      !isWithinDeactivationGracePeriod(data.family.endDate)
    ) {
      navigate(
        withReviewParam(
          memberSelfServeReactivationPath(familyStatus),
          reviewRequested,
        ),
      );
    }
  }, [data, navigate, reviewRequested]);

  useEffect(() => {
    if (!data?.family) return;
    const previewStatus = getPreviewStatus(data.family);
    if (previewStatus.isExpired) {
      navigate("/preview-expired");
    }
  }, [data, navigate]);

  return (
    <MemberDetailsContext.Provider value={contextValue}>
      {children}
    </MemberDetailsContext.Provider>
  );
};
